import { type FunctionComponent, useCallback } from 'react';

import { NavPages } from '../components/nav/navPages/NavPagesConnected';
import { PopupType } from '../components/popup/types';
import { useAudioTrigger } from '../controllers/audioController/hooks';
import { useGameRedirectConnected } from '../hooks/useGameRedirectConnected';
import { useShouldStayConnected } from '../hooks/useShouldStayConnected';
import { useCoreDispatch } from '../store/CoreStore';
import { useConfigSlice } from '../store/slices/config/selectors';
import { gameStateActions } from '../store/slices/game/slice';
import { headerActions } from '../store/slices/header/slice';
import { useMiniLobbySlice } from '../store/slices/minilobby/selectors';
import { miniLobbyActions } from '../store/slices/minilobby/slice';
import { popupActions } from '../store/slices/popup/slice';
import { settingsActions } from '../store/slices/settings/slice';
import { useIsLargerThanWide } from '../styles/hooks/media';
import { getWindowObject } from '../utils/window';

import { CoreLobbyMessageController, hostInfoMessage } from './CoreLobbyMessageController';
import { MessageReceiver } from './MessageReceiver';
import type { SoundMessagePayload } from './messages/sound/types';
import type { IncomingMessage } from './types';

export const LobbyMessageReceiver: FunctionComponent = () => {
  const dispatch = useCoreDispatch();
  const tableId = useConfigSlice.session()?.tableId;
  const audioTrigger = useAudioTrigger();
  const isLobbyFullscreen = useMiniLobbySlice.getIsLobbyFullscreen();
  const gameRedirect = useGameRedirectConnected();
  const isWideScreen = useIsLargerThanWide();
  const shouldStayAtTable = useShouldStayConnected();

  const onLobbyBackClick = useCallback((): void => {
    if (!isWideScreen) dispatch(headerActions.pushNavRoute(NavPages.HOME));
  }, [dispatch, isWideScreen]);

  const handleMiniLobbyMessage = useCallback(
    (message: IncomingMessage): void => {
      if (!tableId) return;
      if (message?.name && message?.group === 'lobby') {
        switch (message.name) {
          case 'toggleOverlay': //V2
          case 'toggleBackToGame': {
            const payload = message?.payload as { isHidden: boolean };
            dispatch(headerActions.setBackToGameHidden(payload.isHidden));
            break;
          }

          // V2 messages
          case 'readyToReceive': {
            CoreLobbyMessageController.post(
              // Check message host value
              hostInfoMessage({ id: tableId, host: 'liveCasino' })
            );
            dispatch(miniLobbyActions.setIsIframeLoaded(true));
            break;
          }

          case 'close': {
            dispatch(headerActions.setMiniLobbyOpen(false));
            dispatch(headerActions.setOpenedModal(null));
            dispatch(headerActions.setNavRoute([]));
            audioTrigger.play({ name: 'menuClick' });
            break;
          }

          case 'open': {
            const payload = message?.payload as { url: string } | undefined;
            const url = payload?.url;

            // Italy Compliance: prevent selecting other tables while in a round with accepted bets.
            if (shouldStayAtTable) {
              dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress));
              return;
            }
            if (!url) return;
            getWindowObject().open(url, '_self');
            break;
          }

          case 'sound': {
            const payload = message?.payload as SoundMessagePayload | undefined;
            if (!payload) return;
            const { masterVolume, channels } = payload;
            dispatch(settingsActions.setMasterVolumeOnly(masterVolume));
            if (channels) {
              channels.forEach((channel) => {
                dispatch(
                  settingsActions.setVolume({
                    channel: channel.name,
                    value: channel.volume,
                  })
                );
              });
            }
            break;
          }

          case 'mute': {
            dispatch(settingsActions.mute());
            break;
          }

          case 'unmute': {
            dispatch(settingsActions.unmute());
            break;
          }
          case 'gameAbandon': {
            dispatch(gameStateActions.setGameAbandoned(true));
            break;
          }

          default:
            break;
        }
      }
    },
    [audioTrigger, dispatch, gameRedirect, isLobbyFullscreen, onLobbyBackClick, tableId, shouldStayAtTable]
  );

  return <MessageReceiver handler={handleMiniLobbyMessage} />;
};
